/**
 * DiscrepancyReportService — Generación de informes de discrepancias y casos pendientes
 *
 * Analiza los resultados del procesamiento batch para identificar infraestructuras
 * con problemas: sin coordenadas, score bajo, discrepancias municipio-coordenada,
 * geocodificación fallida y errores de pipeline.
 *
 * Todas las funciones son puras, sin efectos secundarios.
 *
 * @module services/pipeline/DiscrepancyReportService
 * @version 1.0.0
 * @task F9.03
 */

import type { BatchAggregateReport, MunicipalityResult } from './MunicipalityBatchProcessor';
import type { PipelineOutput } from './CoordinateProcessingPipeline';

// =============================================================================
// TIPOS
// =============================================================================

/** Tipo de discrepancia detectada */
export type DiscrepancyType =
  | 'sin-coordenadas'
  | 'score-bajo'
  | 'centroide-municipal'
  | 'conflicto-referencia'
  | 'discrepancia-centroide'
  | 'geocodificacion-fallida'
  | 'error-pipeline';

/**
 * CENFOR-B (13-sep-2026) — procedencia de la coordenada final de una
 * infraestructura, tal como la deriva `utils/procedenciaVia.ts`. El
 * `PipelineOutput` no la lleva; la aporta el adaptador que conoce la infra
 * (`buildDiscrepancyFromInfras`). Cuando `via === 'centroide'` el caso
 * `score-bajo` se sustituye por `centroide-municipal` con `aviso` como detalle:
 * el técnico filtra por tipo y «score bajo» escondía la causa.
 */
export interface ProcedenciaCaso {
  via: string;
  aviso?: string;
}

/** Opciones de generación (todas opcionales; sin ellas el informe es el de siempre). */
export interface DiscrepancyReportOptions {
  /** id de infraestructura → procedencia (DOP-CENFORB-2=a′). */
  procedencia?: ReadonlyMap<string, ProcedenciaCaso>;
}

/** Severidad del caso */
export type Severity = 'alta' | 'media' | 'baja';

/** Caso individual de discrepancia */
export interface DiscrepancyCase {
  ine5: string;
  nombre: string;
  provincia: string;
  infraId: string;
  infraNombre: string;
  tipo: DiscrepancyType;
  detalle: string;
  severidad: Severity;
}

/** Informe completo de discrepancias */
export interface DiscrepancyReport {
  casos: DiscrepancyCase[];
  porProvincia: Record<string, number>;
  porTipo: Record<string, number>;
  totalCasos: number;
  resumenTexto: string;
}

// =============================================================================
// CONSTANTES
// =============================================================================

/** Umbral de score por debajo del cual se considera bajo */
const SCORE_THRESHOLD = 50;

/** Orden de severidad para priorización (menor = más urgente) */
const SEVERITY_ORDER: Record<Severity, number> = {
  alta: 0,
  media: 1,
  baja: 2,
};

/** Mapa tipo → severidad */
const TYPE_SEVERITY: Record<DiscrepancyType, Severity> = {
  'sin-coordenadas': 'alta',
  'error-pipeline': 'alta',
  'score-bajo': 'media',
  'centroide-municipal': 'media',
  'conflicto-referencia': 'media',
  'geocodificacion-fallida': 'media',
  'discrepancia-centroide': 'baja',
};

// =============================================================================
// FUNCIONES AUXILIARES (PRIVADAS)
// =============================================================================

/** Distancia legible en español: «877 m», «1,8 km» (RAE: coma decimal, espacio antes de la unidad). */
function formatMetros(m: number): string {
  if (m < 1000) return `${Math.round(m)} m`;
  return `${(m / 1000).toFixed(1).replace('.', ',')} km`;
}

/**
 * Extrae discrepancias de un único PipelineOutput dentro de un municipio.
 */
function extractCasesFromOutput(
  output: PipelineOutput,
  munResult: MunicipalityResult,
  procedencia?: ProcedenciaCaso,
): DiscrepancyCase[] {
  const cases: DiscrepancyCase[] = [];
  const esCentroide = procedencia?.via === 'centroide';
  const base = {
    ine5: munResult.ine5,
    nombre: munResult.nombre,
    provincia: munResult.provincia,
    infraId: output.id,
    infraNombre: output.nombre,
  };

  // 1. Sin coordenadas — solo cuando de verdad no hay coordenada final.
  // DISDUP (DOP-DISDUP-1=a): `fuenteCoordenadas === 'ninguna'` dejó de contar
  // aquí. El comparador (caso A.4, `REVISION_MANUAL`, >500 m) devuelve
  // `'ninguna'` y el flujo conserva la coordenada del documento como final:
  // la fila tiene X/Y y el panel la anunciaba como «sin coordenadas» además de
  // «score bajo». Sin coordenadas = sin coordenadas finales, nada más.
  if (output.coordenadasFinales === null) {
    cases.push({
      ...base,
      tipo: 'sin-coordenadas',
      detalle: 'Infraestructura sin coordenadas finales disponibles',
      severidad: TYPE_SEVERITY['sin-coordenadas'],
    });
  }

  // 2a. Centroide municipal (CENFOR-B): sustituye a score-bajo, nunca duplica.
  if (output.coordenadasFinales !== null && esCentroide) {
    cases.push({
      ...base,
      tipo: 'centroide-municipal',
      detalle:
        procedencia?.aviso ??
        'Ubicación forzada al centroide municipal: no es la ubicación del elemento.',
      severidad: TYPE_SEVERITY['centroide-municipal'],
    });
  }

  // 2b. Conflicto con la referencia (DISDUP, DOP-DISDUP-2=b): caso A.4 del
  // comparador — documento y referencia a >500 m; se conserva la coordenada
  // del documento (score 30) para revisión humana. Sustituye a score-bajo,
  // como hace centroide-municipal: «score bajo» escondía la causa.
  const esConflictoReferencia =
    output.coordenadasFinales !== null &&
    !esCentroide &&
    output.flagValidacion === 'REVISION_MANUAL';
  if (esConflictoReferencia) {
    const dist = output.distanciaMetros;
    const distTexto =
      dist !== null && Number.isFinite(dist)
        ? ` (a ${formatMetros(dist)} de la referencia)`
        : '';
    cases.push({
      ...base,
      tipo: 'conflicto-referencia',
      detalle:
        `La coordenada del documento no coincide con la referencia oficial${distTexto}: ` +
        'se conserva la del documento. Revisar y confirmar la ubicación.',
      severidad: TYPE_SEVERITY['conflicto-referencia'],
    });
  }

  // 2c. Score bajo (solo si tiene coordenadas y no es centroide ni conflicto,
  // para no duplicar)
  if (
    output.coordenadasFinales !== null &&
    !esCentroide &&
    !esConflictoReferencia &&
    output.scoreConfianza < SCORE_THRESHOLD
  ) {
    cases.push({
      ...base,
      tipo: 'score-bajo',
      detalle: `Score de confianza: ${output.scoreConfianza}/100 (umbral: ${SCORE_THRESHOLD})`,
      severidad: TYPE_SEVERITY['score-bajo'],
    });
  }

  // 3. Discrepancia centroide
  if (output.validacionCentroide?.warning) {
    cases.push({
      ...base,
      tipo: 'discrepancia-centroide',
      detalle: output.validacionCentroide.warning,
      severidad: TYPE_SEVERITY['discrepancia-centroide'],
    });
  }

  // 4. Geocodificación fallida
  if (!output.geocodificacion.exitoso) {
    cases.push({
      ...base,
      tipo: 'geocodificacion-fallida',
      detalle: `Geocodificación no exitosa (fuente: ${output.geocodificacion.fuente || 'ninguna'})`,
      severidad: TYPE_SEVERITY['geocodificacion-fallida'],
    });
  }

  // 5. Errores pipeline
  if (output.errores.length > 0) {
    cases.push({
      ...base,
      tipo: 'error-pipeline',
      detalle: output.errores.join('; '),
      severidad: TYPE_SEVERITY['error-pipeline'],
    });
  }

  return cases;
}

/**
 * Agrupa conteos por clave string.
 */
function countBy(cases: DiscrepancyCase[], key: 'provincia' | 'tipo'): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const c of cases) {
    const k = c[key];
    counts[k] = (counts[k] ?? 0) + 1;
  }
  return counts;
}

// =============================================================================
// FUNCIONES PÚBLICAS
// =============================================================================

/**
 * Genera un informe de discrepancias a partir de un BatchAggregateReport.
 *
 * Analiza cada PipelineOutput de cada municipio (excluyendo los omitidos)
 * e identifica cinco tipos de discrepancia: sin coordenadas, score bajo,
 * discrepancia centroide, geocodificación fallida y errores de pipeline.
 */
export function generateDiscrepancyReport(
  report: BatchAggregateReport,
  options: DiscrepancyReportOptions = {},
): DiscrepancyReport {
  const allCases: DiscrepancyCase[] = [];

  for (const mun of report.resultadosPorMunicipio) {
    // Excluir municipios skipped (sin infraestructuras / abortados)
    if (mun.status === 'skipped') continue;

    for (const output of mun.outputs) {
      const cases = extractCasesFromOutput(
        output,
        mun,
        options.procedencia?.get(output.id),
      );
      allCases.push(...cases);
    }
  }

  const porProvincia = countBy(allCases, 'provincia');
  const porTipo = countBy(allCases, 'tipo');

  const resumenTexto = buildSummaryText(allCases, porProvincia, porTipo);

  return {
    casos: allCases,
    porProvincia,
    porTipo,
    totalCasos: allCases.length,
    resumenTexto,
  };
}

/**
 * Construye el texto resumen para el informe.
 */
function buildSummaryText(
  casos: DiscrepancyCase[],
  porProvincia: Record<string, number>,
  porTipo: Record<string, number>,
): string {
  if (casos.length === 0) {
    return 'No se detectaron discrepancias. Todos los registros procesados son válidos.';
  }

  const altas = casos.filter((c) => c.severidad === 'alta').length;
  const medias = casos.filter((c) => c.severidad === 'media').length;
  const bajas = casos.filter((c) => c.severidad === 'baja').length;

  return [
    `Total discrepancias: ${casos.length}`,
    `Severidad: ${altas} alta, ${medias} media, ${bajas} baja`,
    `Por tipo: ${Object.entries(porTipo).map(([t, n]) => `${t} (${n})`).join(', ')}`,
    `Por provincia: ${Object.entries(porProvincia).map(([p, n]) => `${p} (${n})`).join(', ')}`,
  ].join('\n');
}

/**
 * Formatea un DiscrepancyReport como texto legible multilínea.
 *
 * Agrupa los casos por provincia y municipio para facilitar la lectura.
 */
export function formatDiscrepancyReport(report: DiscrepancyReport): string {
  const lines: string[] = [];

  lines.push('═══════════════════════════════════════════════════');
  lines.push('  INFORME DE DISCREPANCIAS PTEL');
  lines.push('═══════════════════════════════════════════════════');
  lines.push('');
  lines.push(report.resumenTexto);
  lines.push('');

  if (report.casos.length === 0) {
    return lines.join('\n');
  }

  // Agrupar por provincia → municipio
  const byProv = new Map<string, Map<string, DiscrepancyCase[]>>();
  for (const caso of report.casos) {
    if (!byProv.has(caso.provincia)) {
      byProv.set(caso.provincia, new Map());
    }
    const munMap = byProv.get(caso.provincia)!;
    const munKey = `${caso.ine5} - ${caso.nombre}`;
    if (!munMap.has(munKey)) {
      munMap.set(munKey, []);
    }
    munMap.get(munKey)!.push(caso);
  }

  // Provincias ordenadas
  const provsSorted = [...byProv.keys()].sort();
  for (const prov of provsSorted) {
    lines.push(`── ${prov.toUpperCase()} (${report.porProvincia[prov]} casos) ──`);
    const munMap = byProv.get(prov)!;
    const munsSorted = [...munMap.keys()].sort();

    for (const munKey of munsSorted) {
      const casos = munMap.get(munKey)!;
      lines.push(`  📍 ${munKey}`);
      for (const c of casos) {
        const sev = c.severidad === 'alta' ? '🔴' : c.severidad === 'media' ? '🟡' : '🟢';
        lines.push(`    ${sev} [${c.tipo}] ${c.infraNombre}: ${c.detalle}`);
      }
    }
    lines.push('');
  }

  return lines.join('\n');
}

/**
 * Devuelve los casos que requieren intervención manual, priorizados
 * por severidad (alta → media → baja) y luego por provincia/municipio.
 */
export function getPendingCases(report: DiscrepancyReport): DiscrepancyCase[] {
  return [...report.casos].sort((a, b) => {
    // 1. Por severidad
    const sevDiff = SEVERITY_ORDER[a.severidad] - SEVERITY_ORDER[b.severidad];
    if (sevDiff !== 0) return sevDiff;

    // 2. Por provincia
    const provDiff = a.provincia.localeCompare(b.provincia);
    if (provDiff !== 0) return provDiff;

    // 3. Por municipio
    return a.nombre.localeCompare(b.nombre);
  });
}
